import { ReactNode, useEffect, useState, useMemo } from "react";
import moment from "moment";
import { CompanyOrdersNavs, CurrentOrdersContentTypes, ICurrentOrdersHeaderNavs } from "../../../../../../Interfaces/componentTypes";
import { ICompanyCurrentOrdersResponce, IOrderResponse } from "../../../../../../Interfaces/responseTypes";
import { useGetCompanyFilteredOrdersQuery, useUpdateOrderStatusMutation } from "../../../../../../API/authApi";
import { OrderStatus } from "../../../../../../Interfaces/formValue-interfaces";
import useDialogHooks from "../../../../../../Components/Dialog/dialog-hooks";
import OrderStatusDialog from "../../Dialogs/OrderStatusDialog/orderStatusDialog";
import PrintInvoiceDialog from "../../Dialogs/PrintInvoice/printInvoiceDialog";
import { ICurrentOrdersProps } from "./currentOrders";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";

const useCurrentOrders = (props: ICurrentOrdersProps) => {
    const { t } = useLanguagesHooks();
    const { orders, company_id, QR } = props;
    const today = moment().format("DD-MM-YYYY");
    const [activeContent, setActiveContent] = useState<CurrentOrdersContentTypes>(CurrentOrdersContentTypes.LIST);
    const [activeNav, setActiveNav] = useState<CompanyOrdersNavs>(CompanyOrdersNavs.CONSOLE);
    const [activeOrders, setActiveOrders] = useState<ICompanyCurrentOrdersResponce | IOrderResponse[]>(orders ?? []);
    const [todayOrders, setTodayOrders] = useState<IOrderResponse[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<IOrderResponse | null>(null);
    const [updateOrderStatus] = useUpdateOrderStatusMutation();
    const { data: firstPage } = useGetCompanyFilteredOrdersQuery({ company_id, start_date: today, end_date: today, page: 1 });
    const { open: isDialogOpen, onOpen, onClose } = useDialogHooks();
    const [dialogComponent, setDialogComponent] = useState<ReactNode | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    // ----------------- Handle orders update -----------------
    useEffect(() => {
        if (orders) {
            setActiveOrders(orders);
            if (isFirstLoad) {
                setIsLoading(false);
                setIsFirstLoad(false);
            }
        }
    }, [orders, isFirstLoad]);

    // ----------------- Fetch all today's orders -----------------
    useEffect(() => {
        if (!firstPage) return;
        let allOrders: IOrderResponse[] = [...firstPage.results];
        const totalPages = Math.ceil(firstPage.count / firstPage.results.length);

        const fetchAllPages = async () => {
            for (let page = 2; page <= totalPages; page++) {
                const res = await fetch(`/api/order/company/${company_id}/?start_date=${today}&end_date=${today}&page=${page}`);
                const data = await res.json();
                allOrders = allOrders.concat(data.results);
            }
            setTodayOrders(allOrders);
            if (isFirstLoad) {
                setIsLoading(false);
                setIsFirstLoad(false);
            }
        };

        totalPages > 1 ? fetchAllPages() : setTodayOrders(allOrders);
    }, [firstPage, company_id, today, isFirstLoad]);
    // ----------------- Memoized order filters -----------------
    const succeededOrders = useMemo(() => todayOrders.filter(o => o.status === 'delivered'), [todayOrders]);
    const rejectedOrders = useMemo(() => todayOrders.filter(o => o.status === 'rejected'), [todayOrders]);
    const canceledOrders = useMemo(() => todayOrders.filter(o => o.status === 'cancelled'), [todayOrders]);

    const succeededOrdersTotal = useMemo(() => succeededOrders.reduce((acc, order) => {
        try {
            const parsedData = JSON.parse(order.data);
            return acc + (Number(parsedData.total_cost) || 0);
        } catch {
            return acc;
        }
    }, 0), [succeededOrders]);

    const consoleOrdersCount = useMemo(() => {
        if (!orders) return 0;
        if (Array.isArray(orders)) return orders.length;
        if ('message' in orders) return 0;
        return Object.values(orders).reduce(
            (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
            0
        );
    }, [orders]);

    const hasActiveOrders = useMemo(() => {
        if (!activeOrders) return false;
        if (Array.isArray(activeOrders)) return activeOrders.length > 0;
        return Object.values(activeOrders).some(arr => Array.isArray(arr) && arr.length > 0);
    }, [activeOrders]);

    // ----------------- Navigation handler -----------------
    const onHandleNav = (nav: CompanyOrdersNavs) => {
        const navMap: Record<CompanyOrdersNavs, IOrderResponse[] | ICompanyCurrentOrdersResponce> = {
            [CompanyOrdersNavs.CONSOLE]: orders ?? [],
            [CompanyOrdersNavs.SUCCEEDED]: succeededOrders,
            [CompanyOrdersNavs.REJECTED]: rejectedOrders,
            [CompanyOrdersNavs.CANCELED]: canceledOrders,
        };

        setActiveNav(nav);
        setActiveContent(CurrentOrdersContentTypes.LIST);
        setActiveOrders(navMap[nav]);
        scrollTop();
    };


    // ----------------- Select order -----------------
    const selectOrder = (order: IOrderResponse) => {
        if (order.status === OrderStatus.NEW) {
            updateOrderStatus({
                order_id: order.id,
                body: { company_id, status: OrderStatus.PENDING }
            });
        }
        setSelectedOrder(order);
        setActiveContent(CurrentOrdersContentTypes.ITEM);
    };

    const onBack = () => {
        setSelectedOrder(null);
        setActiveContent(CurrentOrdersContentTypes.LIST);
    };

    // ----------------- Dialogs -----------------
    const onOpenStatusDialog = (order: IOrderResponse, setLocalStatus?: React.Dispatch<React.SetStateAction<OrderStatus>>, currentStatus?: OrderStatus) => {
        setDialogComponent(
            <OrderStatusDialog
                company_id={company_id}
                current_status={currentStatus ?? order.status}
                order_id={order.id}
                onClose={onCloseDialog}
                orderType={order.order_type}
                setLocalStatus={setLocalStatus}
            />
        );
        onOpen();
    };

    const onOpenPrintInvoceDialog = (order: IOrderResponse) => {
        setDialogComponent(<PrintInvoiceDialog order={order} QR={QR} onClose={onCloseDialog} />);
        onOpen();
    };

    const onCloseDialog = () => {
        setDialogComponent(null);
        onClose();
    };
    // ----------------- Navs -----------------
    const currenOrdersNavs: ICurrentOrdersHeaderNavs[] = [
        { id: CompanyOrdersNavs.CONSOLE, title: t('My_Business.Current_Orders.Console'), count: consoleOrdersCount },
        { id: CompanyOrdersNavs.SUCCEEDED, title: t('My_Business.Current_Orders.Done'), count: succeededOrders.length },
        { id: CompanyOrdersNavs.REJECTED, title: t('My_Business.Current_Orders.Rejected'), count: rejectedOrders.length },
        { id: CompanyOrdersNavs.CANCELED, title: t('My_Business.Current_Orders.Canceled'), count: canceledOrders.length },
    ];

    return {
        isLoading,
        activeNav,
        activeOrders,
        isDialogOpen,
        activeContent,
        selectedOrder,
        dialogComponent,
        hasActiveOrders,
        currenOrdersNavs,
        succeededOrdersTotal,
        onBack,
        onHandleNav,
        selectOrder,
        onCloseDialog,
        setActiveContent,
        onOpenStatusDialog,
        onOpenPrintInvoceDialog,
    };
};

export default useCurrentOrders;
